// @ts-nocheck
import ButtonClose from '@components/ButtonClose';
import ColumnLayout from '@components/ColumnLayout';
import Logo from '@components/Logo';
import AuthUserContext from '@context/AuthUserContext';
import { calcFinishSurcharge, calcLeadTimeByQuantity } from '@src/helpers/product';
import { format, addBusinessDays } from 'date-fns';
import Link from '@utility/Link';
import useCartState from '@utility/useCartState';
import { graphql, useStaticQuery } from 'gatsby';
import foreach from 'lodash.foreach';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { addToCartDL, beginCheckoutDL, getItemGroup, getProductCategories, purchaseCompleteDL, removeFromCartDL } from '@src/helpers/dataLayerHelper';
import { useLocation } from '@reach/router';
import FirebaseContext from '@src/context/FirebaseContext';
import { SURCHARGE_ENABLE, SURCHARGE_ID, SURCHARGE_RATE } from '../../../common';

const SURCHARGE_SUFFIX = "-Surcharge";

const snipcartScript = () => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');

    Object.assign(script, {
      src: 'https://cdn.snipcart.com/themes/v3.3.3/default/snipcart.js',
      id: 'snipcart',
      defer: true,
    });
    script.dataset.apiKey = process.env.GATSBY_SNIPCART_APIKEY;
    script.addEventListener('load', () => {
      resolve();
    });
    script.addEventListener('error', (e) => {
      reject(e);
    });

    document.body.appendChild(script);
  });
};

const SnipCart = () => {
  const cartData = useStaticQuery(
    graphql`
      query {
        allSanityCart {
          edges {
            node {
              id
              heading
              subheading
              orderShipDateNote
              image {
                asset {
                  _id
                }
              }
            }
          }
        }
        sanitySupport {
          tradeEligiblePromoCodes
        }
      }
    `,
  );
  const cartContent = cartData.allSanityCart.edges[0].node;
  const noCancelCodes = cartData.sanitySupport?.tradeEligiblePromoCodes || [];
  const [status, setStatus] = useState('start');
  const [distinctItemsLength, setDistinctItemsLength] = useState(0);
  const [discountItemsLength, setDiscountLength] = useState(0);
  const [finalTariffSurchargeAmount, setFinalTariffSurcharge] = useState(0);
  const { authUser } = useContext(AuthUserContext);
  const firebase = useContext(FirebaseContext);
  const cartState = useCartState();
  const location = useLocation();
  const ref = useRef();
  const debounceRef = useRef();

  const isCartConfirmedListenerAttached = useRef(false);
  const addRemoveItemsListenersAttached = useRef(false);
  const reconcileTimerRef = useRef(null);
  const reconcileRunningRef = useRef(false);
  const ignoreNextEventsRef = useRef(0);


  const importSnipcartScript = () => {
    snipcartScript()
      .then(() => {
        setStatus('done');
      })
      .catch(() => {
        setStatus('error');
        //console.log('Snipcart failed to load');
      });
  };

  /**
   * Implemented for v3.0.15 of Snipcart.
   * Snipcart currently does not appear to have a way to remove promo codes out of the box..
   * Using their api to for the moment to allow removal of promo codes.
   * https://docs.snipcart.com/v3/sdk/api#removediscount
   */
  const removePromoCode = useCallback(async (code) => {
    try {
      await window.Snipcart.api.cart.removeDiscount(code);
    } catch (error) {
      //console.log(error);
    }
  }, []);

  useLayoutEffect(() => {
    const MutationObserver =
      window.MutationObserver || window.WebKitMutationObserver;
    const observer = new MutationObserver(() => {
      /**========================================================================
       *                           Discount Box addition/removal
       *========================================================================**/
      //Hide 'Apply promo code box'
      // const discountBox = document.querySelector('.snipcart-discount-box');
      // if (discountBox) {
      //   if (authUser?.user_trade) {
      //     discountBox.classList.add('hidden');
      //   } else {
      //     discountBox.classList.remove('hidden');
      //   }
      // }

      addCheckoutFooterText();

      /**========================================================================
       *                           Initializing Promo Codes
       *========================================================================**/
      const elements = document.querySelectorAll(
        '.snipcart-summary-fees__discount-name',
      );
      const discounts = (
        Snipcart.store.getState().cart.discounts?.items || []
      ).filter(i => i.id !== SURCHARGE_ID).map((item) =>
        item.code ? { code: item.code, name: item.name } : null,
      );

      if (elements && elements.length > 0) {
        foreach(elements, (element) => {
          const code = element.innerHTML;

          const filteredDiscount = discounts.find(
            (discount) => discount?.code === code || discount?.name === code,
          );

          if (!!filteredDiscount) {
            if (
              authUser?.user_trade &&
              !noCancelCodes.includes(filteredDiscount.code)
            ) {
              removePromoCode(filteredDiscount.code);
            } else {
              element.addEventListener('click', () => {
                removePromoCode(filteredDiscount.code);
              });
            }
          } else {
            element.classList.add('no-button');
          }
        });
      }

      /**------------------------------------------------------------------------
       *                           Correcting Inner HTML
       *------------------------------------------------------------------------**/
      const discountAmountElements = document.querySelectorAll(
        '.snipcart-summary-fees__discount-amount-saved',
      );
      if (discountAmountElements && discountAmountElements.length > 0) {
        foreach(discountAmountElements, (discountAmountElement) => {
          if (discountAmountElement.innerHTML === ' -') {
            discountAmountElement.innerHTML = ' -$0.00';
          }
        });
      }
    });

    if (status === 'start') {
      setStatus('ready');
    }

    if (status === "done" && !isCartConfirmedListenerAttached.current) {
      isCartConfirmedListenerAttached.current = true;
  
      Snipcart.events.on('cart.confirmed', (cartState) => {
        console.log('For debugging => Purchase complete', cartState)
        // gtag('event', 'successful_purchase', {
        //   send_to: '7FU0COzI35AZENGQ66U9',
        //   value: cartState.total,
        //   currency: 'USD',
        // });
        let items = cartState?.items?.items?.length > 0
          ? cartState.items.items.map((i) => {
            const productOptions = i.url ? i.url.split('/').pop().split('-') : '';
            const variant = i.categories.length > 0 ? i.categories[0] : '';
            const categories = getProductCategories(productOptions, variant);

            return ({
              item_id: i.id,
              item_name: i.name,
              item_variant: variant,
              item_group: getItemGroup(i.id, variant),
              price: i.price,
              quantity: i.quantity,
              trade_discount: !!authUser?.user_trade,
              ...categories
            })
          })
          : [];

        
        const shippingAddrObj = cartState.shipToBillingAddress ? cartState.billingAddress : cartState.shippingAddress; 
        const coupons = cartState.discounts.items.map(item => item.code).join(',');

        const dl_user = {
          full_name: shippingAddrObj.fullName,
          email: cartState.email,
          phone: shippingAddrObj.phone,
          city: shippingAddrObj.city,
          customer_street: shippingAddrObj.address1,
          customer_state: shippingAddrObj.province,
          province: shippingAddrObj.province,
          zip_code: shippingAddrObj.postalCode,
          country: shippingAddrObj.country,
          user_trade: !!authUser?.user_trade,
          logged_in_user: authUser?.email,
          logged_in_user_id: authUser?.uid,
        }

        const dl_order = {
          shipping: cartState.shippingDetails.cost,
          transaction_id: cartState.invoiceNumber,
          tax: cartState.taxes.items.reduce((sum, item) => sum + item.amount, 0),
          coupon: cartState.shippingDetails.method === 'Free Shipping' ? coupons.concat(`, Free Shipping`) : coupons
        }

        purchaseCompleteDL(items, cartState.subtotal, cartState.total, dl_order, dl_user);


        const checkForAllowMarketingEmail = cartState.customFields.find(x=> x.name === "Consented-to-Marketing-Email")
        console.log("For debugging =>> checkForAllowMarketingEmail", checkForAllowMarketingEmail)
        if(checkForAllowMarketingEmail.value === "true"){
          firebase?.subscribeKlaviyoPromotionalEmails?.({email: cartState.email}).then((response) => {
            console.log(response);
          })
          .catch((err) => console.error(err));
        }
      });
    }

    if (status === 'done' && !addRemoveItemsListenersAttached.current) {
      addRemoveItemsListenersAttached.current = true;
      Snipcart.events.on('page.validating', function (ev, data) {
        if (data.phone.length !== 10) {
          ev.addError(
            'phone',
            'Please enter a US or Canadian phone number without any symbols',
          );
        }
      });

      Snipcart.events.on('discount.applied', (discount) => {
        setTimeout(() => {
          document.querySelector('.snipcart-discount-box__cancel')?.click();
        }, 100);
      });

      Snipcart.events.on('item.removed', (cartConfirmResponse) => {
        removeFromCartDL({
          id: cartConfirmResponse.id,
          name: cartConfirmResponse.name,
          price: cartConfirmResponse.price,
          quantity: cartConfirmResponse.quantity,
          totalPrice: cartConfirmResponse.totalPrice,
          url: cartConfirmResponse.url,
          variant: cartConfirmResponse.categories.length > 0 ? cartConfirmResponse.categories[0] : '',
          trade_discount: !!authUser?.user_trade
        })
      });

      Snipcart.events.on('item.added', (cartConfirmResponse) => {
        addToCartDL({
          id: cartConfirmResponse.id,
          name: cartConfirmResponse.name,
          price: cartConfirmResponse.price,
          quantity: cartConfirmResponse.quantity,
          totalPrice: cartConfirmResponse.totalPrice,
          url: cartConfirmResponse.url,
          variant: cartConfirmResponse.categories.length > 0 ? cartConfirmResponse.categories[0] : '',
          trade_discount: !!authUser?.user_trade
        })
      });

      observer.observe(document, {
        subtree: true,
        childList: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [status, authUser?.user_trade, removePromoCode]);

  useEffect(() => {
    if (location.hash === '#/checkout') {
      let cartItems = Snipcart.store.getState().cart.items?.items || [];
      Snipcart.api.cart.update({
        customFields: [
          {
            name: "User-Logged-In",
            value: authUser?.email
          },
          {
            name: "User-Firebase-Id",
            value: authUser?.uid
          },
          {
            name: "Trade-Discount-Applied",
            value: cartItems?.length && cartItems.some(item => item.id.includes('discount'))
          },
          {
            name: "Consented-to-Marketing-Email",
            value: true
          }
        ]
      });

      let items = cartItems.length > 0
        ? cartItems.map((i) => {
          const variant = i.categories.length > 0 ? i.categories[0] : '';
          const productOptions = i.url.split('/').pop().split('-');
          const categories = getProductCategories(productOptions, variant)

          return {
            item_id: i.id,
            item_name: i.name,
            price: i.price,
            quantity: i.quantity,
            item_group: getItemGroup(i.id, variant),
            item_variant: variant,
            trade_discount: !!authUser?.user_trade,
            ...categories
          }})
        : [];

      beginCheckoutDL(items, Snipcart.store.getState().cart.total);
      removeExtraTariffs();
    }
  }, [location.hash]);


  useEffect(() => {
    if (status === 'done') {
      setupFinishSurchargeReconciler();
    }

    if (status === 'done') {
      const unsubscribe = Snipcart?.store?.subscribe((res) => {
        const state = Snipcart.store.getState();
        const newDiscountLength = state?.cart?.discounts?.items?.length ?? 0;
        
        //Update surcharge value if a discount was added or removed
        setDiscountLength(prev => {
          if (prev !== newDiscountLength) {
            return newDiscountLength;
          }
          return prev;
        });

        document.querySelectorAll('.snipcart-payment-methods-list-item').forEach((li) => {
          const button = li.querySelector('button[title="Checkout with Pay later"]');
        
          if (button) {
            const state = Snipcart.store.getState();
            const hasShowroomDiscount =
              state.cart?.discounts?.items?.length > 0 &&
              state.cart.discounts.items.some(d => d.code === 'SHOWROOM');
        
            if (hasShowroomDiscount) {
              li.style.display = '';
            } else {
              li.style.display = 'none';
            }
          }
        });
      });
    
      return () => {
        unsubscribe?.();
      };
    }
    
  }, [status]);

  useEffect(() => {
    if (status === 'done') {
      setTimeout(() => {
        updateSurcharge();
      }, 300);
    }
  }, [discountItemsLength]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (finalTariffSurchargeAmount == null) return; // skip if undefined

    const Snipcart = window.Snipcart;
    if (!Snipcart?.api || !Snipcart?.store) return;

    const lockRef = { current: false }; // simple local lock

    const pushTariffToSnipcart = async () => {
      if (lockRef.current) return; // prevent overlap
      lockRef.current = true;

      try {
        const cart = Snipcart.store.getState().cart;
        const existing = cart.items.items.find(i => i.id === SURCHARGE_ID);
        const price = Number(finalTariffSurchargeAmount.toFixed(2));

        // Small wait before we read again, in case Snipcart is mid-update
        await new Promise(r => setTimeout(r, 100));

        const refreshedCartItems = Snipcart.store.getState().cart;
        const existingSurchargeItem = refreshedCartItems.items.items.find(i => i.id === SURCHARGE_ID);

        if (price === 0 && existingSurchargeItem) {
          await Snipcart.api.cart.items.remove(existingSurchargeItem.uniqueId);
        } else if (existingSurchargeItem) {
          await Snipcart.api.cart.items.update({
            uniqueId: existingSurchargeItem.uniqueId,
            price: price,
            url: `/.netlify/functions/tariffSurcharge?price=${price}`,
          });
        } else if (price > 0) {
          await Snipcart.api.cart.items.add({
            id: SURCHARGE_ID,
            name: 'Tariff Surcharge',
            price: price,
            quantity: 1,
            url: `/.netlify/functions/tariffSurcharge?price=${price}`,
            customFields: [],
            shippable: false
          });
        }
      } catch (err) {
        console.error("Error updating surcharge:", err);
      } finally {
        lockRef.current = false;
      }
    };

    // Debounce: wait until surcharge value settles before calling
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(pushTariffToSnipcart, 3000);

    return () => clearTimeout(debounceRef.current);
  }, [finalTariffSurchargeAmount]);


  useEffect(() => {
    const controller = new AbortController();
    let isCancelled = false;
    (async () => {
      if (status === 'done' && cartState?.hasItems) {
        const cartProducts = Snipcart.store
          .getState()
          .cart.items.items
          .map(
            ({ uniqueId, id, url, customFields, price, quantity, name }) => ({
              uniqueId,
              id,
              url: normalizeProductUrl(url),
              customFields,
              price,
              quantity,
              name
            }),
          );

        if (cartProducts.length > 0) {
          const updatePromises = cartProducts.map(async (product) => {
            if (product.url.includes('finishes')) return;

            if (product.id === SURCHARGE_ID) return;

            if (product.id.toLowerCase().includes('surcharge')) return;

            try {
              const response = await fetch(product.url, {
                signal: controller.signal,
              });
              const htmlText = await response.text();

              const data = JSON.parse(htmlText);
              const isTrade = !!authUser?.user_trade;
              const id = isTrade
                ? !product.id.includes('discount')
                  ? `${product.id}-discount`
                  : product.id
                : product.id.replace(/-discount$/, '');
              //* swapping cart items to match user trade status
              const correctPrice = data[isTrade ? 'discount_price' : 'real_price'];
              const updatedFields = [...product.customFields].map((field, i) =>
                i === 1 ? { ...field, value: JSON.stringify(isTrade) } : field
              );
              const skuUrl = `/.netlify/functions/validateSku?price=${correctPrice}&id=${`${id}`}&name=${product.name}&discount_price=${data?.discount_price}&real_price=${data?.real_price}`
              return Snipcart.api.cart.items.update({
                uniqueId: product.uniqueId,
                id,
                price: correctPrice,
                customFields: updatedFields,
                url: location.origin + skuUrl,
              });
            } catch (err) {
              if (!isCancelled) console.error(`Failed to update cart item ${product.id}`, err);
            }
          });

          await Promise.all(updatePromises);
          updateSurcharge();
        }
      }
    })();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [authUser?.user_trade, cartState?.hasItems, status]);

  useEffect(() => {
    handleCartItemsChange();
  }, [cartState?.quantity]);

  if (status === 'ready' && ref.current) {
    importSnipcartScript();
  }

  const removeExtraTariffs = async () => { //Remove tailing tariff items due to async
    const allSurcharges = Snipcart.store.getState().cart.items.items.filter(i => i.id === SURCHARGE_ID);
    let startIndex = 1;
    if (!allSurcharges.length) return;
    if (!SURCHARGE_ENABLE) startIndex = 0; //if tariff is not enabled, this should remove any tariffs that has already been applied while it was enabled
    if (allSurcharges.length > startIndex) {
      for (let i = startIndex; i < allSurcharges.length; i++) {
        await Snipcart.api.cart.items.remove(allSurcharges[i].uniqueId);
      }
      location.reload();
    }
  }

  const getCustomFieldValue = (item, name) =>
    item?.customFields?.find((e) => e.name === name)?.value;

  const findTotalPriceOfExtendedFinishProducts = (product, excludeThisProduct) => { 
    // DOC: If excludeThisProduct is true, then this product price will not be taken into account. 
    // excludeThisProduct is to be used when a product is removed, but another product of same id and finish remains(with different SKU)
    // excludeThisProduct, will only be true, in case of removefinishsurcharge execution
    const partialSkuWithSecond = splitProductIdBy2(product.id);
    const secondaryFinishCodePC = getCustomFieldValue(product, "secondaryFinishCodePC");

    if (!secondaryFinishCodePC) return;


    const finishSurchargeID = getFinishSurchageID(product, secondaryFinishCodePC);

    const cart = Snipcart.store.getState().cart;
    let cartItems = cart?.items?.items ?? [];

    // Find all items in cart that match same partial SKU + same secondaryFinishCodePC
    const extendedFinishItems = cartItems.filter((item) => {
      if (item.id === SURCHARGE_ID) return false; // ignore surcharge items if they exist
      if (excludeThisProduct && item.id === product.id) return false;
      const itemPartialSku = splitProductIdBy2(item.id);
      const itemSecondary = getCustomFieldValue(item, "secondaryFinishCodePC");
      return itemPartialSku === partialSku && itemSecondary === secondaryFinishCodePC;
    });

    // Combined base price (in cents) + combined quantity
    const totalExtendedProductsPrice = extendedFinishItems.reduce((sum, item) => {
      const totalPrice = Math.round((Number(item.totalPrice) || 0));
      return sum + totalPrice;
    }, 0);

    return totalExtendedProductsPrice;

  }

  const handleCartItemsChange = async () => {
    if (status !== 'done') return;
    const cartProducts = Snipcart.store?.getState()?.cart.items.items || [];

    if (!SURCHARGE_ENABLE) removeExtraTariffs();

    //Update shipdateText on quantity change
    const updatePromises = cartProducts.map(async (product) => {
      if (product.url.includes('finishes')) return;

      if (product.id === SURCHARGE_ID) return;
      if (product.id.toLowerCase().includes('surcharge')) return;

      try {
        const updatedFields = updateShipDateTextInCustomFields([...product.customFields], product.quantity).map((field, i) =>
          i === 1 ? { ...field, value: JSON.stringify(!!authUser?.user_trade) } : field
        );
        //DOC: Shipdate text seems to be getting updated even without calling the below API, wierd
        // return Snipcart.api.cart.items.update({
        //   uniqueId: product.uniqueId,
        //   customFields: updatedFields,
        // }).then(res => console.log(res));
      } catch (err) {
        console.error(`Failed to update cart item ${product.id}`, err);
      }
    });

    await Promise.all(updatePromises);

    //update surcharge once all other items are synced
    setTimeout(() => {
      updateSurcharge();
    }, 300);
    

    //update checkout text in cart
    onItemsChange();
  }

  const getShipDateText = (from, to, quantity, shipDateOverride) => {
    let shipdateTextTemp = ''
    if (quantity > 200) {
      shipdateTextTemp = 'Please contact RBW for lead time info'
    }
    else if (shipDateOverride) shipdateTextTemp = shipDateOverride;
    else shipdateTextTemp = `Available to ship between ${ formatDate(from) } - ${ formatDate(to) }`;
    return shipdateTextTemp;
  }

  const formatDate = (date) => {
    return format(date, 'MMM d');
  };

  const updateShipDateTextInCustomFields = (customFields, quantity) => {
    let shipDateOverride = customFields.find((e) => e.name === 'shipDateOverride')?.value;
    let minLeadTime = customFields.find((e) => e.name === 'minLeadTime')?.value;
    let leadTimeRange = {};

    if (!shipDateOverride && !minLeadTime) {
      return customFields;
    }

    if (customFields.findIndex((e) => e.name === 'Accessory') !== -1) {
      leadTimeRange = {
        min: parseInt(customFields.find((e) => e.name === 'minLeadTime')?.value),
        max: parseInt(customFields.find((e) => e.name === 'maxLeadTime')?.value)
      }
    } else {
      let d = {
        minLeadTime: parseInt(customFields.find((e) => e.name === 'minLeadTime').value) || 0,
        shipDateOverride: shipDateOverride,
        maxLeadTime: parseInt(customFields.find((e) => e.name === 'maxLeadTime').value) || 0,
        leadTime10: parseInt(customFields.find((e) => e.name === 'leadTime10').value) || 0,
        leadTime25: parseInt(customFields.find((e) => e.name === 'leadTime25').value) || 0,
        leadTime50: parseInt(customFields.find((e) => e.name === 'leadTime50').value) || 0,
        leadTime100: parseInt(customFields.find((e) => e.name === 'leadTime100').value) || 0,
        leadTime200: parseInt(customFields.find((e) => e.name === 'leadTime200').value) || 0,
      }

      leadTimeRange = calcLeadTimeByQuantity(Number.isNaN(quantity) ? 0 : quantity, d);
    }

    let addedLeadTimeDays = parseInt(customFields.find((e) => e.name === 'addedLeadTimeDays')?.value) || 0
    let updatedCustomFields = customFields.slice();
    let fromDate = addBusinessDays(new Date(), leadTimeRange.min + addedLeadTimeDays)
    let toDate = addBusinessDays(new Date(), leadTimeRange.max + addedLeadTimeDays)
    let shipdateText = getShipDateText(fromDate, toDate, quantity, shipDateOverride);

    let estIndex = updatedCustomFields.findIndex(a => a.name === 'shipDateEstimate');
    if (estIndex >= 0) {
      updatedCustomFields[estIndex].value = `${shipdateText}`;
      updatedCustomFields[estIndex].displayValue = `${shipdateText}`;
    }
    return updatedCustomFields;
  }

  //Check if no of distict items > 2, then add the orderShipDateNote
  const onItemsChange = () => {
    const cartItems = Snipcart.store
      .getState()
      .cart.items.items
      .filter(item => item.id !== SURCHARGE_ID);
  
    const distinctItemIds = new Set(cartItems.map(item => item.id));
    const count = distinctItemIds.size;
  
    setDistinctItemsLength(count);
    addCheckoutFooterText(count);
  };

  const addCheckoutFooterText = (itemLength) => {
    // Select the footer element
    const footer = document.querySelector('.snipcart-cart__footer-buttons');

    let length = itemLength !== null || itemLength !== undefined ? itemLength : distinctItemsLength;

    // Check if the footer element exists
    if (footer) {
      const button = footer.querySelector('button.snipcart-button-primary');

      // Check if the button exists and its title is "Checkout"
      if (button && button.textContent.trim() === 'Checkout') {
        // Check if the <p> tag is already added
        let paragraph = footer.querySelector('p');

        // If <p> tag doesn't exist, create and append it
        if (!paragraph) {
          paragraph = document.createElement('p');
          paragraph.classList.add('mt-4', '!text-mono-500', '!type-sans-030');
          paragraph.textContent = cartContent.orderShipDateNote;
          button.parentNode.insertBefore(paragraph, button.nextSibling);
        }
      } else {
        // Select and remove the <p> tag if the button title is not "Checkout"
        const paragraph = footer.querySelector('p');
        if (paragraph) {
          paragraph.parentNode.removeChild(paragraph);
        }
      }
    }
  }

  const updateSurcharge = async () => {
    if (!SURCHARGE_ENABLE) return;

    const cart = Snipcart.store.getState().cart;

    // Calculate total of taxable products
    const productsTotal = cart.items.items
      .filter(item => item.id !== SURCHARGE_ID)
      .reduce((sum, item) => sum + item.price * item.quantity, 0);
    
    //calculate the discounts deduction
    const discountedValue = cart.discounts.items
      .reduce((sum, item) => sum + item.value, 0);
    const productsTotalAfterDiscounts = Math.max(0, productsTotal + discountedValue); //adding discountedValue as it is expected to be a negative value
    
    const surchargeAmount = parseFloat((productsTotalAfterDiscounts * SURCHARGE_RATE).toFixed(2));

    const existingSurcharge = cart.items.items.find(item => item.id === SURCHARGE_ID);
    setFinalTariffSurcharge(surchargeAmount);
  };

  //helper functions

  const normalizeProductUrl = (url) => {
    // if (process.env.GATSBY_DEVELOPMENT_MODE === 'true') {
    //   return `https://rbw-qa.netlify.app${new URL(url).pathname}`;
    // }
    return url;
  };

  // ---- event wiring
  function setupFinishSurchargeReconciler() {
    const handler = (evtName) => (payload) => {
      const isTriggeredBySurchageItem = (payload && isSurchargeLine(payload)) || payload?.id === SURCHARGE_ID;

      if (ignoreNextEventsRef.current > 0) {
        ignoreNextEventsRef.current--;
        return;
      }
      scheduleReconcile(evtName, isTriggeredBySurchageItem);
    };

    Snipcart.events.on("item.added", handler("item.added"));
    Snipcart.events.on("item.removed", handler("item.removed"));
    Snipcart.events.on("item.updated", handler("item.updated"));

    // Initial pass after Snipcart is ready
    if (status === 'done') {
      scheduleReconcile("snipcart.ready")
    }
  }

  function scheduleReconcile(reason = "unknown", isTriggeredBySurchageItem) {
    // Coalesce multiple rapid events (add/remove/update)
    clearTimeout(reconcileTimerRef.current);
    reconcileTimerRef.current = setTimeout(() => reconcileSurcharges(reason, isTriggeredBySurchageItem), 150);
  }

  const getSecondaryFinishCodePC = (item) => {
    // Works if Snipcart gives customFields as array (like your earlier snippet)
    const v = item?.customFields?.find((f) => f?.name === "secondaryFinishCodePC")?.value;
    // Sometimes it’s also in item.customFields.secondaryFinishCodePC in some setups
    return v;
  };

  const isSurchargeLine = (item) =>
    typeof item.id === "string" && item.id.endsWith(SURCHARGE_SUFFIX);

  const splitProductIdBy2 = (itemId) => {
    if (!itemId) return '';
    const idSplitArray = itemId.split('-');
    return idSplitArray[0] + '-' + idSplitArray[1];
  }

  const getFinishSurchageID = (item, secondaryFinishCodePC) =>
    `${splitProductIdBy2(item.id)}-${secondaryFinishCodePC}${SURCHARGE_SUFFIX}`;

  const getRealPriceFromUrl = (url) => {
    const params = new URL(url).searchParams;
    const realPrice = params.get("real_price");

    if (realPrice) {
      return parseFloat(realPrice);
    }

    return 0;
  }

  async function reconcileSurcharges(reason = "unknown", isTriggeredBySurchageItem) {
    if (isTriggeredBySurchageItem) return;
    if (reconcileRunningRef.current) return;
    reconcileRunningRef.current = true;

    try {
      // Let Snipcart store finish applying the change that triggered the event
      const cartInitial = Snipcart.store.getState().cart?.items.items ?? [];
      await new Promise((r) => setTimeout(r, cartInitial.length > 1 ? 150 : 2000));

      const cart = Snipcart.store.getState().cart;
      const items = cart?.items?.items ?? [];

      // surchargeId -> { baseTotalMajor, meta }
      const grouped = new Map();

      for (const item of items) {
        if (isSurchargeLine(item)) continue;

        const code = getSecondaryFinishCodePC(item);
        if (!code) continue;

        const surchargeId = getFinishSurchageID(item, code);

        const unitPriceMajor = getRealPriceFromUrl(item.url);
        const qty = Number(item.quantity ?? 1);
        const lineTotalMajor = unitPriceMajor * qty;
        const partialSku = item.customFields?.find((e) => e.name === "partial_sku")?.value
        const existing = grouped.get(surchargeId);

        if (!existing) {
          // capture meta once (you said it's same for the group anyway)
          grouped.set(surchargeId, {
            baseTotalMajor: lineTotalMajor,
            meta: {
              secondaryFinishImagePC: item.customFields?.find((e) => e.name === "secondaryFinishImagePC")?.value,
              secondaryFinishTitlePC: item.customFields?.find((e) => e.name === "secondaryFinishTitlePC")?.value,
              finishUpcharge: calcFinishSurcharge(lineTotalMajor, partialSku),
              partialSku,
              productName: item.name,
            },
          });
        } else {
          existing.baseTotalMajor += lineTotalMajor;
        }
      }

      // desiredSurcharges: surchargeId -> { priceMajor, meta }
      const desiredSurcharges = new Map();
      for (const [surchargeId, { baseTotalMajor, meta }] of grouped.entries()) {
        const surchargeMajor = calcFinishSurcharge(baseTotalMajor, meta.partialSku); // returns major units
        if (Number(surchargeMajor) > 0) {
          desiredSurcharges.set(surchargeId, { priceMajor: surchargeMajor, meta });
        }
      }

      // 2) Find existing surcharge lines
      const existingSurcharges = items.filter(isSurchargeLine);

      // 3) Remove surcharge lines that are no longer needed
      for (const s of existingSurcharges) {
        if (!desiredSurcharges.has(s.id)) {
          ignoreNextEventsRef.current++;
          await Snipcart.api.cart.items.remove(s.uniqueId);
        }
      }

      // 4) Add/update desired surcharge lines
      // Re-fetch after removals to avoid stale uniqueIds / store mismatch
      await new Promise((r) => setTimeout(r, 0));
      const cart2 = Snipcart.store.getState().cart;
      const items2 = cart2?.items?.items ?? [];
      const existingById = new Map(items2.filter(isSurchargeLine).map((x) => [x.id, x]));

      for (const [surchargeId, { priceMajor, meta }] of desiredSurcharges.entries()) {
        const existing = existingById.get(surchargeId);

        if (existing) {
          // Update only if changed (prevents extra loops)
          const currentMajor = Number(existing.price ?? 0);
          // Compare at 2 decimals to avoid float noise
          const same =
            Math.round((currentMajor + Number.EPSILON) * 100) ===
            Math.round((Number(priceMajor) + Number.EPSILON) * 100);

          if (!same) {
            ignoreNextEventsRef.current++;
            await Snipcart.api.cart.items.update({
              uniqueId: existing.uniqueId,
              price: Number(priceMajor),
              url: `/.netlify/functions/tariffSurcharge?price=${Number(priceMajor)}&id=${encodeURIComponent(
                surchargeId
              )}`
            });
          }
        } else {
          const secondaryFinishImagePC = meta.secondaryFinishImagePC;
          const secondaryFinishTitlePC = meta.secondaryFinishTitlePC;
          const finishUpcharge = meta.finishUpcharge;
          const partialSku = meta.partialSku;
          const productName = meta.productName;

          ignoreNextEventsRef.current++;
          await Snipcart.api.cart.items.add({
            id: surchargeId,
            name: "Finish Surcharge",
            description: `${productName} in ${secondaryFinishTitlePC}`,
            image: secondaryFinishImagePC,
            price: Number(priceMajor),
            quantity: 1,
            categories: ["Finish Surcharge"],
            url: `/.netlify/functions/tariffSurcharge?price=${Number(priceMajor)}&id=${encodeURIComponent(
              surchargeId
            )}`,
            dimensions: { weight: 10 },
            customFields: [
              {
                type: "hidden",
                name: "Actual Price",
                value: finishUpcharge ?? "",
                options: "",
                required: false,
              },
              {
                type: "hidden",
                name: "isUserTrade",
                value: "false",
                options: "",
                required: false,
              },
              {
                type: "hidden",
                name: "partialSku",
                value: partialSku ?? "",
                options: "",
                required: false,
              },
              {
                type: "hidden",
                name: "productUrl",
                value: "javascript:void(0)",
                options: "",
                required: false,
              },
            ],
            shippable: false,
            taxable: true,
            stackable: "never",
          });
        }
      }
    } finally {
      reconcileRunningRef.current = false;
    }
  }

  
  return (
    <>
      { (status === 'ready' || status === 'done') && (
        <div
          hidden
          id="snipcart"
          data-api-key={ process.env.GATSBY_SNIPCART_APIKEY }
          data-config-add-product-behavior="none"
          ref={ ref }
        >
          {/* * CART HEADER */ }
          <cart-header
            title=""
            showItemsCount="false"
            showAccountMenu="false"
            showSummary="false"
            showAccountLink="false"
            backButtonTitle=""
          >
            <div className="root">
              <div className="wrapper">
                <div className="snipcart-cart-header">
                  <Link to="/" styled={ false } title="RBW" aria-label="RBW Logo">
                    <Logo />
                  </Link>
                  <close-cart-action class="snipcart-cart-header__close-button snipcart-modal__close">
                    <ButtonClose aria-label="Close" />
                  </close-cart-action>
                </div>
              </div>
            </div>
          </cart-header>

          {/* * EMPTY CART */ }
          <empty-cart>
            <div className="root">
              <section className="snipcart-empty-cart">
                <ColumnLayout
                  heading={ cartContent.heading }
                  subheading={ cartContent.subheading }
                  image={ cartContent?.image }
                >
                  <Link
                    to="/type"
                    className="btn btn-default btn-black"
                    styled={ false }
                    aria-label="Browse Products"
                  >
                    Browse Products
                  </Link>
                </ColumnLayout>
              </section>
            </div>
          </empty-cart>

          {/* * BILLING FORM */ }
          <billing>
            <div class="snipcart__box">
              <div class="snipcart__box--header">
                <div class="snipcart__box--title">
                  <h1 class="snipcart__font--subtitle">Billing</h1>
                </div>
              </div>

              <snipcart-form-error></snipcart-form-error>

              <overridable name="billing" section="top"></overridable>

              <fieldset class="snipcart-form__set">
                <div class="snipcart-form__field">
                  <snipcart-label class="snipcart__font--tiny" for="name">
                    Full Name
                  </snipcart-label>

                  <snipcart-input
                    name="name"
                    pattern="^[a-zA-Z]([-']?[a-zA-Z]+)*( [a-zA-Z]([-']?[a-zA-Z]+)*)+$"
                    title="Please enter your full name without symbols"
                  ></snipcart-input>
                  <snipcart-field-error name="name"></snipcart-field-error>
                </div>

                <div class="snipcart-form__field">
                  <snipcart-label class="snipcart__font--tiny" for="email">
                    Email
                  </snipcart-label>

                  <snipcart-input name="email"></snipcart-input>
                  <snipcart-field-error name="email"></snipcart-field-error>
                </div>
              </fieldset>

              <address-fields type="billing"></address-fields>

              <hr class="snipcart-form__separator" v-if="shippingEnabled" />

              <fieldset
                class="snipcart-form__set snipcart-form__set--different-shipping"
                v-if="shippingEnabled && !hasOnlyNonShippableGoods"
              >
                <div class="snipcart-form__field">
                  <div class="snipcart-form__field-checkbox">
                    <snipcart-checkbox name="useDifferentShippingAddress" />

                    <snipcart-label
                      class="snipcart__font--tiny snipcart-form__label--checkbox"
                      for="useDifferentShippingAddress"
                    >
                      Use different shipping address
                    </snipcart-label>
                  </div>
                </div>
              </fieldset>
              <overridable name="billing" section="bottom"></overridable>

              <div class="snipcart-form__footer">
                <snipcart-submit label="Continue to Shipping"></snipcart-submit>
              </div>
            </div>
          </billing>

          {/* * SHIPPING ADDRESS FORM */ }
          <shipping-address>
            <div className="root">
              <div class="snipcart__box">
                <div class="snipcart__box--header">
                  <div class="snipcart__box--title">
                    <h1 class="snipcart__font--subtitle">Shipping</h1>
                  </div>
                </div>

                <div class="snipcart-shipping-address--readonly">
                  <div class="snipcart-shipping-address__header--readonly">
                    <h3 class="snipcart-shipping-address__subtitle snipcart__font--bold"></h3>
                  </div>
                </div>

                <overridable
                  name="shipping-address"
                  section="top"
                ></overridable>

                <snipcart-label for="name">Full Name</snipcart-label>
                <snipcart-input
                  name="name"
                  pattern="^[a-zA-Z]([-']?[a-zA-Z]+)*( [a-zA-Z]([-']?[a-zA-Z]+)*)+$"
                  title="Please enter your full name without symbols"
                ></snipcart-input>
                <snipcart-field-error name="name"></snipcart-field-error>

                <address-fields type="shipping"></address-fields>

                <overridable
                  name="shipping-address"
                  section="bottom"
                ></overridable>
                <div class="snipcart-form__footer">
                  <snipcart-submit label="Use This Address"></snipcart-submit>
                </div>
              </div>
            </div>
          </shipping-address>
          <address-fields type="shipping">
            <div className="root">
              <overridable name="address-fields" section="top"></overridable>
              <fieldset class="snipcart-form__set">
                <div class="snipcart-form__row">
                  <div class="snipcart-form__field snipcart-form__cell--large">
                    <snipcart-label class="snipcart__font--tiny" for="address1">
                      Street Address
                    </snipcart-label>
                    <snipcart-input
                      name="address1"
                      pattern="(?!.*(?:(.*([Pp][-.\s]*[Oo][-.\s]+)))).*"
                      title="We currently do not offer shipping to a PO Box, please enter a physical address"
                    ></snipcart-input>
                    <snipcart-field-error name="address1"></snipcart-field-error>
                  </div>

                  <div class="snipcart-form__field snipcart-form__cell--tidy">
                    <snipcart-label class="snipcart__font--tiny" for="address2">
                      Apt/Suite
                    </snipcart-label>
                    <snipcart-input name="address2"></snipcart-input>
                    <snipcart-field-error name="address2"></snipcart-field-error>
                  </div>
                </div>

                <div class="snipcart-form__field">
                  <snipcart-label class="snipcart__font--tiny" for="city">
                    City
                  </snipcart-label>
                  <snipcart-input name="city"></snipcart-input>
                  <snipcart-field-error name="city"></snipcart-field-error>
                </div>

                <div class="snipcart-form__field">
                  <snipcart-label class="snipcart__font--tiny" for="country">
                    Country
                  </snipcart-label>
                  <snipcart-typeahead
                    type="dropdown"
                    name="country"
                    autocomplete="country"
                  ></snipcart-typeahead>
                  <snipcart-field-error name="country"></snipcart-field-error>
                  <p className='!text-blue type-sans-030 mt-2'>Online orders are currently available to ship within the United States</p>
                </div>

                <div class="snipcart-form__row">
                  <div class="snipcart-form__field snipcart-form__cell--large">
                    <snipcart-label class="snipcart__font--tiny" for="province">
                      State
                    </snipcart-label>
                    <snipcart-typeahead
                      type="dropdown"
                      name="province"
                      autocomplete="province state"
                    ></snipcart-typeahead>
                    <snipcart-field-error name="province"></snipcart-field-error>
                  </div>

                  <div class="snipcart-form__field snipcart-form__cell--tidy">
                    <snipcart-label
                      class="snipcart__font--tiny"
                      for="postalCode"
                    >
                      Postal/Zip Code
                    </snipcart-label>
                    <snipcart-input name="postalCode"></snipcart-input>
                    <snipcart-field-error name="postalCode"></snipcart-field-error>
                  </div>
                </div>
              </fieldset>
              <overridable name="address-fields" section="bottom"></overridable>
            </div>
          </address-fields>

          {/* * PHONE NUMBER ADDITION */ }
          <address-fields section="top">
            <div className="snipcart-form__field">
              <snipcart-label for="phone">Phone number</snipcart-label>

              <snipcart-input
                name="phone"
                type="tel"
                pattern="^[-\d]\d*$"
                title="Enter phone number without any symbols"
                required
              ></snipcart-input>
              <snipcart-field-error name="phone"></snipcart-field-error>
            </div>
          </address-fields>

          {/* * CART ITEMS */ }
          <item-line tag="" item="" showDescription="">
            <div className="snipcart-item-line">
              <flash-message
                type="info"
                icon="box"
                v-if="stockLimitReached"
                { ...{ ':title': "$localize('errors.quantity_revised')" } }
              ></flash-message>
              <flash-message
                type="error"
                icon="box"
                v-if="outOfStock"
                { ...{ ':title': "$localize('errors.quantity_out_of_stock')" } }
              ></flash-message>

              <div class="snipcart-item-line__container">
                <figure class="snipcart-item-line__media" v-if="showLargeImage">
                  <a { ...{ ':href': "item.categories.length > 0 && item.categories[0] !== 'Accessory' && item.categories[0] !== 'Finish Sample' ? item.customFields.find(e => e.name === 'productUrl')?.value : item.url" } }>
                    <item-image class="snipcart-item-line__image"></item-image>
                  </a>
                </figure>

                <div class="snipcart-item-line__product">
                  <div class="snipcart-item-line__header">
                    <figure
                      class="snipcart-item-line__media snipcart-item-line__media--small"
                      v-if="showSmallImage"
                    >
                      <a { ...{ ':href': 'item.url' } }>
                        <item-image class="snipcart-item-line__image"></item-image>
                      </a>
                    </figure>

                    <a { ...{ ':href': 'item.url' } }>
                      <h2 class="snipcart-item-line__title snipcart__font--xlarge snipcart__font--secondary snipcart__font--black">
                        { '{{ item.name }}' }
                      </h2>
                    </a>

                    <button-icon
                      icon="trash"
                      label="item.remove_item"
                      styling="danger"
                      { ...{
                        'v-on:click': 'removeItem',
                        'v-if': '!item.id.toLowerCase().includes("surcharge")'
                      }}
                    ></button-icon>
                  </div>

                  <div class="snipcart-item-line__content">
                    <div class="snipcart-item-line__body">
                      <div class="snipcart-item-line__info">
                        <a { ...{ ':href': 'item.url' } }>
                          <item-description v-if="showDescription"></item-description>
                        </a>
                      </div>
                      <div class="snipcart-item-line__variants">
                        <div className='snipcart-item-custom-fields' v-if="!adding">
                          <div v-for="(f, i) in item.customFields" className='snipcart-item-custom-fields__field snipcart-item-custom-fields--textbox' v-if="f.type === 'textbox'">
                            <label className='snipcart-form__label snipcart__font--tiny snipcart-custom-field-wrapper__label'>
                              {
                                "{{f.name}}"
                              }
                            </label>
                          </div>
                          
                          <div className='snipcart-item-custom-fields__field snipcart-item-custom-fields--textbox' v-if="item.customFields.findIndex(e => e.name === 'shipDateEstimate') !== -1">
                            <label className='snipcart-form__label snipcart__font--tiny snipcart-custom-field-wrapper__label'>
                              Estimated Ship Date
                            </label>
                          </div>
                          <div className='snipcart-item-custom-fields__field snipcart-item-custom-fields--textbox' v-if="item.customFields.findIndex(e => e.name === 'shipDateEstimate') !== -1">
                            <label className='snipcart-form__label snipcart__font--tiny snipcart-custom-field-wrapper__label'>
                              {
                                "{{item.customFields.find(e => e.name === 'shipDateEstimate').value}}"
                              }
                            </label>
                          </div>
                        </div>
                        

                        <item-quantity
                          class="snipcart-item-line__quantity"
                          v-if="!adding && !isSubscribable && !item.id.toLowerCase().includes('surcharge')"
                          { ...{ ':disabled': 'outOfStock || isSubscribable' } }
                        ></item-quantity>
                        <div class="snipcart-item-quantity__unit-price">
                          <p
                            class="snipcart-item-quantity__unit-price-label"
                            v-if="item.customFields[1].value==='true'"
                          >
                            Trade Price
                          </p>
                          <p
                            class="snipcart-item-quantity__unit-price-label"
                            v-if="item.customFields[1].value!=='true'"
                          >
                            Price
                          </p>
                          <p
                            class="snipcart-item-quantity__unit-price-discounted"
                            v-if="item.customFields[1].value!=='true' || +item.customFields[0].value===item.unitPrice"
                          >
                            {
                              "{{(+item.unitPrice).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2, minimumFractionDigits: 2}).replace(/\\.00$/g, '')}}"
                            }
                          </p>
                          <p
                            class="snipcart-item-quantity__unit-price-discounted !text-green"
                            v-if="item.customFields[1].value==='true' && +item.customFields[0].value!==item.unitPrice"
                          >
                            {
                              "{{(+item.unitPrice).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2, minimumFractionDigits: 2}).replace(/\\.00$/g, '')}}"
                            }
                          </p>
                        </div>

                        <div
                          class="snipcart-item-quantity__unit-price-container"
                          v-if="+item.customFields[0].value!==item.unitPrice"
                        >
                          <p
                            class="snipcart-item-quantity__unit-price-price"
                            v-if="+item.customFields[0].value!==item.unitPrice"
                          >
                            {
                              "{{(+item.customFields[0].value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2, minimumFractionDigits: 2}).replace(/\\.00$/g, '')}}"
                            }
                          </p>
                        </div>
                        <div class="type-sans-030 mt-4" v-if="item.id.toLowerCase().includes('surcharge')">All Custom Color Sales Final</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </item-line>

          

          <shipping-method section="bottom">
            <p
              class="snipcart-shipping__disclaimer"
              { ...{ 'v-if': 'cart.items.items.length > 1' } }
            >
              Fixtures may arrive in partial shipments
            </p>
          </shipping-method>

          <shipping-completed context="">
            <div className="root">
              <div class="snipcart__box snipcart-shipping-completed">
                <div class="snipcart-shipping-completed__header snipcart__box--header">
                  <div class="snipcart__box--title">
                    <div
                      class="snipcart__box--badge snipcart__font--bold snipcart__font--secondary"
                      v-if="checkout"
                    >
                      <icon name="checkmark" class="snipcart__icon"></icon>
                    </div>
                    <h1 class="snipcart-shipping-completed__title snipcart__font--subtitle">
                      <icon
                        name="box"
                        class="snipcart__icon--left"
                        v-if="!checkout"
                      ></icon>
                      { "{{ $localize('shipping.title') }}" }
                    </h1>
                  </div>
                  <button-link
                    v-if="checkout"
                    label="actions.edit"
                    { ...{ '@click': 'edit' } }
                  ></button-link>
                </div>

                <div class="snipcart-checkout-step__cols snipcart__font--std">
                  <div class="snipcart-checkout-step__col">
                    <div class="snipcart-checkout-step__icon snipcart-shipping-completed__step-icon">
                      <icon name="location"></icon>
                    </div>
                    <div class="snipcart__address__display">
                      <h3 class="snipcart-checkout-step__title snipcart-shipping-completed__step-title snipcart__font--secondary snipcart__font--bold">
                        { "{{ $localize('shipping.address') }}" }
                      </h3>
                      <span class="snipcart-shipping-completed__information snipcart__font--std">
                        { '{{ cart.shippingAddress | fullAddress }}' }
                      </span>
                    </div>
                  </div>

                  <div class="snipcart-checkout-step__col">
                    <div class="snipcart-checkout-step__icon snipcart-shipping-completed__step-icon">
                      <icon name="box"></icon>
                    </div>
                    <div>
                      <h3 class="snipcart-checkout-step__title snipcart-shipping-completed__step-title snipcart__font--secondary snipcart__font--bold">
                        { "{{ $localize('shipping.method') }}" }
                      </h3>
                      <span class="snipcart-shipping-completed__information">
                        {
                          '{{ localizationId ? $localize(localizationId) : shippingDetails.method }}'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <p
                  class="snipcart-shipping__disclaimer"
                  { ...{ 'v-if': 'cart.items.items.length > 1' } }
                >
                  Fixtures may arrive in partial shipments
                </p>
              </div>
            </div>
          </shipping-completed>

          <summary-fees>
            <div class="snipcart-summary-fees">
              <div class="snipcart-summary-fees__notice snipcart__font--regular" { ...{ 'v-if' : '!summaryData.hasShippingAddress'
                } }>
                { "{{ $localize('checkout.shipping_taxes_calculated_when_address_provided') }}" }
              </div>
              <div class="snipcart-summary-fees">
                <div
                    class="snipcart-summary-fees__item snipcart-summary-fees__discounts snipcart__font--slim"
                    v-if="summaryData.hasDiscounts"
                >
                    <span class="snipcart-summary-fees__title">
                        {"{{ $localize('cart_summary.discount') }}"}
                        <div class="snipcart-summary-fees__discounts-icon">
                            <icon
                                name="question-mark"
                                {...{
                                  '@mouseover.native': 'displayDiscounts = true',
                                  '@mouseleave.native': 'displayDiscounts = false'
                                }}
                            ></icon>
                            <tool-tip
                                v-show="displayDiscounts"
                                class="snipcart-tool-tip--bordered snipcart-summary-fees__tool-tip"
                            >
                                <div
                                    class="snipcart-summary-fees__discount-container"
                                    {...{
                                      'v-for': 'discount in summaryData.discounts',
                                      ':key': 'discount.id'
                                    }}
                                >
                                    <div class="snipcart-summary-fees__discount-name">{"{{ discount.name }}"}</div>
                                    <div
                                        class="snipcart-summary-fees__discount-amount-saved"
                                        v-if="!displayDiscount(discount)"
                                    > -{"{{ discount.amountSaved | money(summaryData.currency)}}"}</div>
                                </div>
                            </tool-tip>
                        </div>
                    </span>
                    <span
                        class="snipcart-summary-fees__amount"
                        v-if="summaryData.discountTotalAmount"
                    >{"{{ summaryData.discountTotalAmount | money(summaryData.currency) }}"}</span>
                </div>
                <div class="snipcart-summary-fees__item snipcart__font--slim" { ...{ 'v-if' : 'summaryData.subtotal' } }>
                  <span class="snipcart-summary-fees__title">{ "{{ $localize('cart_summary.subtotal') }}" }</span>
                  <span class="snipcart-summary-fees__amount">{ "{{ summaryData.subtotal | money(summaryData.currency) }}"
                    }</span>
                </div>

                <div class="snipcart-summary-fees__item snipcart__font--slim" { ...{ 'v-if' : 'summaryData.hasShipping' } }>
                  <span class="snipcart-summary-fees__title">{ "{{ $localize('cart_summary.shipping') }}" }</span>
                  <span class="snipcart-summary-fees__amount">{ "{{ summaryData.shippingInformation.cost | money(summaryData.currency) }}" }</span>
                </div>

                <div class="snipcart-summary-fees__item snipcart__font--slim" { ...{ 'v-if' : 'summaryData.hasTaxes' } }>
                  <span class="snipcart-summary-fees__title">{ "{{ $localize('cart_summary.taxes') }}" }</span>
                  <span class="snipcart-summary-fees__amount">{ "{{ summaryData.taxTotalAmount | money(summaryData.currency) }}" }</span>
                </div>   

                <div class="snipcart-summary-fees__item snipcart__font--slim" { ...{ 'v-if' : 'summaryData.cart.items.items.find(d => d.id === "tariffSurcharge")?.price && window.location.hash === "#/cart" && summaryData.cart.total > 0' } }>
                  <span class="snipcart-summary-fees__title">Tariff Surcharge (5.7%)</span>
                  <span class="snipcart-summary-fees__amount">{"{{summaryData.cart.items.items.find(d => d.id === 'tariffSurcharge')?.price | money(summaryData.currency)}}"}</span>
                </div>

                <div
                  class="snipcart-summary-fees__item snipcart-summary-fees__total snipcart__font--bold snipcart__font--secondary">
                  <span class="snipcart-summary-fees__title snipcart-summary-fees__title--highlight snipcart__font--large">
                    { "{{ $localize('cart_summary.total') }}" }
                  </span>
                  <span class="snipcart-summary-fees__amount snipcart-summary-fees__amount--highlight snipcart__font--large">
                    { "{{ summaryData.total | money(summaryData.currency) }}" }
                  </span>
                </div>
              </div>
            </div>
          </summary-fees>

          <payment section="top">
            <div>
              <p class="snipcart-privacy-policy">
                <snipcart-checkbox
                  name="Agree-to-Terms-and-Condition"
                  required
                  title="Please check this box to proceed"
                />
                <snipcart-label for="Agree-to-Terms-and-Condition">
                  I have read and agree to the{ ' ' }
                  <a href="/terms-&-conditions">Terms & Conditions</a>
                </snipcart-label>
              </p>

              <p class="snipcart-subscribe-checkbox">
                <snipcart-checkbox
                  name="Consented-to-Marketing-Email"
                  title="Subscribe to our email list"
                />
                <snipcart-label for="Consented-to-Marketing-Email">
                  I would like to receive promotional emails
                </snipcart-label>
              </p>
            </div>
          </payment>

          <cart-summary-item>
            <div className="root">
              <slot v-bind="item">
                <li class="snipcart-cart-summary-item">
                  <span class="snipcart-cart-summary-item__name snipcart__font--slim">
                    { '{{ item.name }}' }
                  </span>

                  <span class="snipcart-cart-summary-item__quantity snipcart__font--slim">
                    {
                      "{{ $localize('cart_summary.quantity') }}{{ item.quantity }}"
                    }
                  </span>

                  <span class="snipcart-cart-summary-item__price snipcart__font--slim">
                    {
                      '{{(+item.totalPrice).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2, minimumFractionDigits: 2}).replace(/\\.00$/g, "")}}'
                    }
                  </span>
                </li>
              </slot>
            </div>
          </cart-summary-item>

          <cart-summary-expanded-item>
            <div className="root">
              <li class="snipcart-cart-summary-expanded-item">
                <div>
                  <span class="snipcart-cart-summary-expanded-item__name snipcart__font--secondary snipcart__font--regular">
                    { '{{ item.name }}' }
                  </span>
                  <ul class="snipcart-cart-summary-expanded-item__custom-fields">
                    <li
                      v-for="(customField, i) in visibleCustomFields"
                      { ...{ ':key': 'i' } }
                    >
                      { '{{ customField.name }}' }: { '{{ customField.value }}' }
                    </li>
                  </ul>
                </div>
                <div class="snipcart-cart-summary-expanded-item--secondary snipcart-cart-summary-expanded-item__quantity">
                  {
                    "{{ $localize('item.quantity_short') }}: {{ item.quantity }}"
                  }
                </div>
                <div class="snipcart-cart-summary-expanded-item--secondary snipcart-cart-summary-expanded-item__price snipcart__font--secondary snipcart__font--bold">
                  {
                    '{{(+item.totalPrice).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2, minimumFractionDigits: 2}).replace(/\\.00$/g, "")}}'
                  }
                </div>
              </li>
            </div>
          </cart-summary-expanded-item>
        </div>
      ) }
    </>
  );
};

export default SnipCart;
